"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@repo/shared/utils";
import { useConstComponent } from "./hooks/useNestedComponent";
import { Tooltip, type TooltipProps } from "./Tooltip";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        destructiveAlt:
          "bg-destructive-alternate text-destructive-foreground hover:bg-destructive-alternate/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        tertiary: "bg-tertiary text-tertiary-foreground hover:bg-tertiary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-8 w-8",
        xs: "h-6 rounded px-2 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];
export type ButtonSize = VariantProps<typeof buttonVariants>["size"];

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = Object.assign(
  React.forwardRef<
    HTMLButtonElement,
    ButtonProps & {
      tooltip?: React.ReactNode;
      tooltipProps?: TooltipProps;
      tooltipWrapperStyles?: string;
    }
  >(
    (
      {
        className,
        variant,
        size,
        asChild = false,
        type = "button",
        tooltip,
        tooltipWrapperStyles,
        tooltipProps,
        ...props
      },
      ref
    ) => {
      const Comp = useConstComponent(asChild ? Slot : "button");
      const jsx = (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          type={type}
          {...props}
        />
      );
      return tooltip == null ? (
        jsx
      ) : (
        <Tooltip label={tooltip} asChild {...tooltipProps}>
          <div className={tooltipWrapperStyles}>{jsx}</div>
        </Tooltip>
      );
    }
  ),
  { variants: buttonVariants }
);
Button.displayName = "Button";
